import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, Eye, EyeOff, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { circlesDivisionsService, CircleDivision } from '../../services/api/circlesDivisionsService';

const CirclesDivisionsList: React.FC = () => {
  const navigate = useNavigate();
  const [officers, setOfficers] = useState<CircleDivision[]>([]);
  const [filteredOfficers, setFilteredOfficers] = useState<CircleDivision[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toDelete, setToDelete] = useState<CircleDivision | null>(null);
  const [stats, setStats] = useState({
    totalCircles: 0,
    totalSuperintendingEngineers: 0,
    totalExecutiveEngineers: 0,
    totalFACOfficers: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...officers];
    if (levelFilter) {
      filtered = filtered.filter((o) => o.level_type === levelFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.name.toLowerCase().includes(term) ||
          o.designation.toLowerCase().includes(term) ||
          o.location?.toLowerCase().includes(term) ||
          o.contact_number?.includes(term),
      );
    }
    setFilteredOfficers(filtered);
  }, [searchTerm, levelFilter, officers]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [flatRes, statsRes] = await Promise.all([
        circlesDivisionsService.getFlat(),
        circlesDivisionsService.getStatistics(),
      ]);
      setOfficers(flatRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await circlesDivisionsService.delete(toDelete.id);
      toast.success('Record deleted successfully');
      setShowDeleteModal(false);
      setToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete record');
      console.error(error);
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await circlesDivisionsService.toggleStatus(id);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading circles & divisions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Circles & Divisions</h1>
          <p className="mt-1 text-sm text-gray-600">Manage PRED organizational structure</p>
        </div>
        <button
          onClick={() => navigate('/circles-divisions/create')}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Add Officer
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">Total Circles</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalCircles}</p>
            </div>
            <div className="p-2 rounded-lg bg-blue-100">
              <Building2 size={20} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">S.E. Officers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalSuperintendingEngineers}</p>
            </div>
            <div className="p-2 rounded-lg bg-green-100">
              <Building2 size={20} className="text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">E.E. Officers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalExecutiveEngineers}</p>
            </div>
            <div className="p-2 rounded-lg bg-purple-100">
              <Building2 size={20} className="text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600">FAC Officers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalFACOfficers}</p>
            </div>
            <div className="p-2 rounded-lg bg-yellow-100">
              <Building2 size={20} className="text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, designation, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="">All Levels</option>
            <option value="superintending_engineer">Superintending Engineers</option>
            <option value="executive_engineer">Executive Engineers</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Circle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOfficers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              ) : (
                filteredOfficers.map((officer) => (
                  <tr key={officer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {officer.circle_number}
                      {officer.sub_number && <span className="text-gray-500 ml-1">({officer.sub_number})</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{officer.name}</div>
                      {officer.is_fac && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-purple-800 bg-purple-100 rounded">
                          FAC
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{officer.designation}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{officer.location || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{officer.contact_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold ${
                          officer.level_type === 'superintending_engineer'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {officer.level_type === 'superintending_engineer' ? 'S.E.' : 'E.E.'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(officer.id)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          officer.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {officer.is_active ? (
                          <>
                            <Eye size={12} className="mr-1" /> Active
                          </>
                        ) : (
                          <>
                            <EyeOff size={12} className="mr-1" /> Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => navigate(`/circles-divisions/edit/${officer.id}`)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setToDelete(officer);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{toDelete?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CirclesDivisionsList;
